import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Loader2, 
  Eye, 
  EyeOff, 
  Check, 
  ArrowRight, 
  Truck, 
  Calendar, 
  Users, 
  FileText,
  Shield,
  Clock,
  BarChart3,
  Gift
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const FreeTrial = () => {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const previousThemeRef = useRef(theme);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    workEmail: '',
    password: ''
  });
  
  // Validation state
  const [errors, setErrors] = useState({});

  // Force dark mode on this page
  useEffect(() => {
    previousThemeRef.current = theme;
    if (theme !== 'dark') {
      setTheme('dark');
    }
    return () => {
      if (previousThemeRef.current !== 'dark') {
        setTheme(previousThemeRef.current);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Password validation
  const passwordChecks = {
    length: form.password.length >= 8,
    number: /\d/.test(form.password)
  };
  const passwordValid = passwordChecks.length && passwordChecks.number;

  // Email validation
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!form.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!form.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!form.workEmail.trim()) {
      newErrors.workEmail = 'Work email is required';
    } else if (!isValidEmail(form.workEmail)) {
      newErrors.workEmail = 'Please enter a valid email address';
    }
    if (!form.password) {
      newErrors.password = 'Password is required';
    } else if (!passwordValid) {
      newErrors.password = 'Password does not meet requirements';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/trial/signup`, {
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        email: form.workEmail.trim().toLowerCase(),
        password: form.password
      });
      
      // Store the pending signup token and redirect to OTP verification
      sessionStorage.setItem('pending_signup_token', response.data.pending_token);
      sessionStorage.setItem('pending_signup_email', form.workEmail.trim().toLowerCase());
      
      toast.success('Verification code sent!', {
        description: 'Please check your email for the 6-digit code.'
      });
      
      navigate('/verify-email');
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to start signup. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Features list for the right panel
  const features = [
    { icon: Truck, text: 'Asset Management' },
    { icon: Calendar, text: 'Job Scheduling & Allocation' },
    { icon: Users, text: 'Operator Management' },
    { icon: FileText, text: 'Digital Dockets' },
    { icon: Shield, text: 'Compliance Tracking' },
    { icon: Clock, text: 'Timesheets' },
    { icon: BarChart3, text: 'Reports & Analytics' }
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Signup Form */}
      <div className="w-full lg:w-[45%] bg-[#1a1a1a] flex flex-col justify-center px-6 sm:px-10 lg:px-12 py-6 relative">
        {/* Subtle texture overlay */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }} />
        
        <div className="max-w-md mx-auto w-full relative z-10">
          {/* Logo */}
          <div className="mb-4">
            <div className="w-12 h-12 rounded-xl overflow-hidden mb-3">
              <img src="/equipr-logo.png" alt="EQUIPR" className="w-full h-full object-cover" />
            </div>
            <h1 className="text-2xl font-heading font-bold text-white mb-1">
              Start Your Free Trial
            </h1>
            <p className="text-zinc-400 text-sm">
              Get instant access to EQUIPR CORE. No credit card required.
            </p>
          </div>
          
          {/* Trial Badge */}
          <div className="mb-4 inline-flex items-center gap-2 bg-[#ffd108]/10 text-[#ffd108] px-3 py-1.5 rounded-full border border-[#ffd108]/20">
            <Gift className="h-3.5 w-3.5" />
            <span className="text-xs font-medium">14-day free trial on Advanced plan</span>
          </div>

          {/* Signup Form */}
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="firstName" className="text-zinc-300 text-sm">First Name</Label>
                <Input
                  id="firstName"
                  data-testid="trial-first-name"
                  value={form.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  placeholder="John"
                  className={`bg-zinc-900 border-zinc-800 focus:border-[#ffd108] h-9 ${
                    errors.firstName ? 'border-red-500' : ''
                  }`}
                />
                {errors.firstName && (
                  <p className="text-red-500 text-xs">{errors.firstName}</p>
                )}
              </div>
              <div className="space-y-1">
                <Label htmlFor="lastName" className="text-zinc-300 text-sm">Last Name</Label>
                <Input
                  id="lastName"
                  data-testid="trial-last-name"
                  value={form.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  placeholder="Smith"
                  className={`bg-zinc-900 border-zinc-800 focus:border-[#ffd108] h-9 ${
                    errors.lastName ? 'border-red-500' : ''
                  }`}
                />
                {errors.lastName && (
                  <p className="text-red-500 text-xs">{errors.lastName}</p>
                )}
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="workEmail" className="text-zinc-300 text-sm">Work Email</Label>
              <Input
                id="workEmail"
                type="email"
                data-testid="trial-work-email"
                value={form.workEmail}
                onChange={(e) => handleInputChange('workEmail', e.target.value)}
                placeholder="john@yourcompany.com"
                className={`bg-zinc-900 border-zinc-800 focus:border-[#ffd108] h-9 ${
                  errors.workEmail ? 'border-red-500' : ''
                }`}
              />
              {errors.workEmail && (
                <p className="text-red-500 text-xs">{errors.workEmail}</p>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="password" className="text-zinc-300 text-sm">Create a Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  data-testid="trial-password"
                  value={form.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="Min 8 characters"
                  className={`bg-zinc-900 border-zinc-800 focus:border-[#ffd108] pr-10 h-9 ${
                    errors.password ? 'border-red-500' : ''
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {/* Password requirements */}
              {form.password && (
                <div className="flex gap-4 mt-1">
                  <div className={`flex items-center gap-1 text-xs ${passwordChecks.length ? 'text-green-500' : 'text-zinc-500'}`}>
                    <Check className={`h-3 w-3 ${passwordChecks.length ? '' : 'opacity-50'}`} />
                    8+ characters
                  </div>
                  <div className={`flex items-center gap-1 text-xs ${passwordChecks.number ? 'text-green-500' : 'text-zinc-500'}`}>
                    <Check className={`h-3 w-3 ${passwordChecks.number ? '' : 'opacity-50'}`} />
                    1 number
                  </div>
                </div>
              )}
              {errors.password && !form.password && (
                <p className="text-red-500 text-xs">{errors.password}</p>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full btn-volt py-5 text-sm"
              disabled={loading}
              data-testid="trial-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating your account...
                </>
              ) : (
                <>
                  Instant Access
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </form>

          {/* Footer Links */}
          <div className="mt-4 pt-4 border-t border-zinc-800">
            <p className="text-zinc-500 text-sm text-center">
              Already have an account?{' '}
              <Link to="/login" className="text-[#ffd108] hover:underline">
                Log In
              </Link>
            </p>
          </div>

          <p className="text-zinc-600 text-xs text-center mt-3">
            By signing up, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>

      {/* Right Panel - Dashboard Preview / Features */}
      <div className="hidden lg:flex lg:w-[55%] bg-gradient-to-br from-gray-100 via-gray-50 to-white relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-10 right-10 w-48 h-48 bg-[#ffd108]/20 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-32 h-32 bg-[#ffd108]/10 rounded-full blur-2xl" />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 py-6">
          <div className="max-w-lg">
            <h2 className="text-xl font-heading font-bold text-gray-900 mb-2">
              Everything you need to run your equipment business
            </h2>
            <p className="text-gray-600 text-sm mb-4">
              From scheduling to dockets, compliance to timesheets — manage it all from one platform.
            </p>
            
            {/* Features List - 2 columns on larger screens */}
            <div className="grid grid-cols-2 gap-2">
              {features.map((feature, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center gap-3 p-2.5 rounded-lg bg-zinc-900 border border-zinc-800 shadow-md"
                >
                  <div className="p-1.5 bg-[#ffd108] rounded-md flex-shrink-0">
                    <feature.icon className="h-4 w-4 text-zinc-900" />
                  </div>
                  <span className="text-white text-sm font-medium">{feature.text}</span>
                </div>
              ))}
            </div>

            {/* Trust Indicators */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-center gap-10">
                <div className="text-center">
                  <div className="text-2xl font-bold text-zinc-900">14</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Days Free</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-zinc-900">No</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Credit Card</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-zinc-900">5 min</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Setup Time</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FreeTrial;
